import { ctx, W, H } from '../canvas.js';
import { state } from '../state.js';
import { isAttract } from '../state.js';
import { t } from '../i18n.js';
import { clamp, rand, pixelRect } from '../utils.js';
import { mods } from '../modes.js';
import {
  AIRFORCE_ESCORT_DROP,
  AIRFORCE_ESCORT_SIDE,
  AIRFORCE_GAP_MAX_MS,
  AIRFORCE_GAP_MIN_MS,
  AIRFORCE_SPEED,
  BATSIGNAL_GAP_MAX_MS,
  BATSIGNAL_GAP_MIN_MS,
  BATSIGNAL_GLOW,
  BATSIGNAL_HERO_DELAY_MS,
  BATSIGNAL_HERO_DROP,
  BATSIGNAL_HERO_EXIT_MS,
  BATSIGNAL_LIFE_MS,
  BATSIGNAL_SPREAD,
  BATSIGNAL_Y,
  BANNER_GAP_MAX_MS,
  BANNER_GAP_MIN_MS,
  BANNER_COUNT,
  BANNER_HEIGHT,
  BANNER_SPEED,
  BANNER_TOW,
  BANNER_WIDTH,
  ISS_ALPHA,
  ISS_GAP_MAX_MS,
  ISS_GAP_MIN_MS,
  METEOR_GAP_MAX_MS,
  METEOR_GAP_MIN_MS,
  METEOR_LIFE_MS,
  METEOR_TAIL,
  ROCKET_GAP_MAX_MS,
  ROCKET_GAP_MIN_MS,
  ROCKET_PUFF_EVERY_MS,
  ROCKET_PUFF_LIFE_MS,
  SKYLINE_HEIGHT,
  SKY_TURN_SECONDS,
  STAR_DIM,
} from '../config.js';
import { record } from '../progress.js';
import { moonAt, moonCraters } from '../moon.js';

/**
 * Un detalle del cielo que mide un píxel, al grosor que se veía cuando el lienzo medía 960.
 *
 * El mundo creció a 1280 y en pantalla todo se dibuja a 0,8: una raya de un píxel —la franja de un
 * avión, el borde de la pancarta, el panel de la ISS— cae por debajo del píxel de pantalla y o no
 * se ve o parpadea. Las partes gordas no se tocan: dos píxeles siguen siendo dos píxeles, y ahí lo
 * que cambia es la distancia, que es justo lo que se pidió.
 *
 * Y se sube a 2 y no a 1,33 a propósito: este arte está dibujado en una rejilla de píxel entero, y
 * crecerlo una fracción lo deja a medio píxel, que se ve peor que pequeño.
 */
const fino = (px) => (px === 1 ? 2 : px);

/**
 * El color de una estrella, ya apagado.
 *
 * La merma va en el color y no en la transparencia, y ese cambio tiene un motivo medido: con alfa
 * el resultado se mezcla con el cielo —que no es negro— y el 15% que se pidió se quedaba en un
 * 13%. Así la estrella tiene el brillo que se pidió, y la merma es la misma para la normal y para
 * la dorada, que sigue parpadeando porque el parpadeo es su alfa y no su color.
 *
 * Los números de partida son `#7682b2` —la normal— y el oro del parpadeo, cada uno por STAR_DIM.
 */
const STAR = `rgb(${Math.round(118 * STAR_DIM)}, ${Math.round(130 * STAR_DIM)}, ${Math.round(178 * STAR_DIM)})`;
const GOLD = [255, 215, 106].map(v => Math.round(v * STAR_DIM));
/** Where the wind arrow runs, and how long it gets. Moved down from 70 when the scoreboard grew:
 *  the wind pill in the HUD now reaches y=67 and was taking the top of the arrow's head with it. */
const WIND_BAR = { y: 76, min: 6, max: 40, scale: 2.5 };

export function drawBackground() {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, '#090d22');
  g.addColorStop(0.55, '#172044');
  g.addColorStop(1, '#5b3156');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // The whole sky slides west, one slow turn of the night, derived from the clock rather
  // than accumulated frame by frame — the same way the moon's travel is, so a pause
  // moves nothing and no star can drift out of step with the others.
  //
  // A drift and not a wheel. A window this size on a turn this slow is a drift to within
  // a pixel a minute, and a drift is the one motion that can leave by one edge and come
  // back in at the other, which is what a night sky does. A rigid rotation would empty
  // the field at the edge it turned away from and never put anything back.
  const slid = (state.clock / 1000 / SKY_TURN_SECONDS) * W;

  state.stars.forEach(s => {
    const tw = (Math.sin(performance.now() / 900 + s.ph) + 1) / 2;
    // Dos y tres, y no uno y dos: un punto de luz tiene que medir más de un píxel de pantalla para
    // verse, y aquí todo se dibuja a 0,8. Las dos tallas van escritas a mano y no con `fino`
    // porque con aquélla las dos caían en dos y el campo salía de estrellas idénticas, que es la
    // otra mitad de lo que se reportó: medio efecto de un cielo es que las haya de dos tamaños.
    const size = s.big ? 3 : 2;
    const x = ((s.x - slid) % W + W) % W;
    pixelRect(x, s.y, size, size, s.gold
      ? `rgba(${GOLD[0]}, ${GOLD[1]}, ${GOLD[2]}, ${0.5 + tw * 0.5})`
      : STAR);
  });

  // Where it is comes from moon.js, which is also what the collision check asks,
  // and it drifts and turns, so there is nothing here to cache.
  const moon = moonAt();

  if (moon) {
    ctx.fillStyle = '#ffe5a4';
    ctx.beginPath();
    ctx.arc(moon.x, moon.y, moon.r, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#a99a85';
    ctx.globalAlpha = 0.18;
    moonCraters(moon).forEach(c => {
      ctx.beginPath();
      ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalAlpha = 1;
  }

  // One switch for the whole passing sky, read here as well as in updateSky: a mode
  // change leaves everything already up on screen untouched, and updateSky returns
  // early from then on, so a meteor or a signal would sit there frozen for good.
  // CLASSIC keeps the original's sky, stars and nothing else — y el escaparate no es CLASSIC:
  // ahí el cielo es el anuncio, así que se enseña siempre.
  if (mods().sky || isAttract()) {
    drawRocket();
    drawMeteors();
    drawIss();
    drawBanner();
    drawAirforce();
    drawBatsignal();
  }

  drawSkyline();
  drawWindIndicator();
}

function drawSkyline() {
  state.skyline.forEach((s, i) => {
    // Hasta el suelo, y no sólo del horizonte hacia arriba. Las torres del fondo acababan en la
    // línea del horizonte y por debajo quedaba un canto recto a la vista: en el mundo de 1280 la
    // ciudad de delante ya no lo tapa, y el corte se veía cruzar el cuadro. Estiradas hasta `H`
    // se meten detrás de los edificios de verdad, que es donde tienen que estar, y su altura
    // sobre la línea no cambia —que es lo que dibuja el horizonte—.
    const top = H - SKYLINE_HEIGHT - s.h;
    pixelRect(s.x, top, 35, H - top, '#11172e');

    // Lit windows, spaced down the face of each silhouette.
    for (let yy = H - 130 - s.h; yy < H - 10; yy += 15) {
      if ((i + yy) % 3 === 0) pixelRect(s.x + 7, yy, 4, 5, '#38466d');
    }
  });
}

// Arrow at the top of the screen showing wind strength and direction.
//
// A still line with a head on it is a diagram of the wind rather than the wind: nothing about a
// fixed arrow says that the air is moving. Three dashes run along it now, from the tail towards
// the head, and they run faster the harder it blows — which is the one thing the length of the
// arrow cannot say, because a long arrow and a fast wind look exactly alike.
//
// On the game clock rather than the wall clock, so a pause stops them with everything else, and
// short arrows get none: at a strength of two or three the span is narrower than the dash and the
// whole thing would read as noise. No current in a breeze is also the honest picture.
function drawWindIndicator() {
  const wx = W / 2;
  const wy = WIND_BAR.y;
  const wl = clamp(Math.abs(state.wind) * WIND_BAR.scale, WIND_BAR.min, WIND_BAR.max) * (state.wind < 0 ? -1 : 1);
  const ah = state.wind < 0 ? -1 : 1;

  ctx.strokeStyle = '#8fe3d0';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(wx - wl, wy);
  ctx.lineTo(wx + wl, wy);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(wx + wl, wy);
  ctx.lineTo(wx + wl - 8 * ah, wy - 5);
  ctx.lineTo(wx + wl - 8 * ah, wy + 5);
  ctx.closePath();
  ctx.fillStyle = '#8fe3d0';
  ctx.fill();

  const span = Math.abs(wl) * 2;
  if (span < 14) return;

  // One dash every third of the arrow, all of them carried towards the head at the speed the air
  // is going, and wrapping round to the tail: what leaves by the head is what the tail hands over.
  //
  // The stride is `wl * 2` and not the span, because `wl` is negative when the wind blows the other
  // way: the tail is at `wx - wl` either way, and going towards the head means going the way `wl`
  // points, not the way a positive number would. With the span there, a leftward wind ran its
  // dashes out beyond the head — measured, both winds carried them right.
  const speed = 16 + Math.abs(state.wind) * 5;
  const phase = (state.clock / 1000) * speed / span;

  ctx.strokeStyle = 'rgba(143, 227, 208, 0.5)';
  ctx.lineWidth = 2;
  for (let i = 0; i < 3; i += 1) {
    const f = (phase + i / 3) % 1;
    const x = wx - wl + f * wl * 2;

    ctx.beginPath();
    ctx.moveTo(x, wy - 4);
    ctx.lineTo(x + 5 * ah, wy - 4);
    ctx.stroke();
  }
}

// --- passing sky -----------------------------------------------------------

/** A streak entering the top of the sky, always heading downwards. */
function newMeteor() {
  const dir = Math.random() < 0.5 ? -1 : 1;
  // The starting x is biased so the streak gets to cross a good part of the
  // sky before it fades out.
  const x = dir > 0 ? rand(-80, W * 0.6) : rand(W * 0.4, W + 80);

  return {
    x,
    // Kept clear of the score panels, which sit across the top corners.
    y: rand(50, 140),
    vx: rand(6, 11) * dir,
    vy: rand(2, 5),
    born: state.clock,
  };
}

/** The station: a slow, almost invisible dot on a shallow diagonal. */
function newIss() {
  const dir = Math.random() < 0.5 ? -1 : 1;

  return {
    x: dir > 0 ? -30 : W + 30,
    // Below the score panels, so a pass is never half hidden behind them.
    y: rand(72, 132),
    vx: (rand(26, 38) / 100) * dir,
    vy: rand(-4, 4) / 100,
  };
}

function advanceMeteors(now) {
  state.meteors.forEach(m => {
    m.x += m.vx;
    m.y += m.vy;
  });
  state.meteors = state.meteors.filter(m => now - m.born < METEOR_LIFE_MS);

  if (now >= state.nextMeteor) {
    state.meteors.push(newMeteor());
    state.nextMeteor = now + skyGap(METEOR_GAP_MIN_MS, METEOR_GAP_MAX_MS);
    record('sky', { id: 'meteor' });
  }
}

function advanceIss(now) {
  const s = state.iss;

  if (!s) {
    if (now >= state.nextIss) {
      state.iss = newIss();
      record('sky', { id: 'iss' });
    }
    return;
  }

  s.x += s.vx;
  s.y += s.vy;

  if (s.x < -40 || s.x > W + 40) {
    state.iss = null;
    state.nextIss = now + skyGap(ISS_GAP_MIN_MS, ISS_GAP_MAX_MS);
  }
}

/**
 * A far rooftop that will actually be seen, for the lamp to stand on and somebody
 * to stand on.
 *
 * The skyline is painted before the city, so the only spires that show are the
 * ones that poke up past the roofs of the foreground towers. A figure on a hidden
 * one would never be seen at all, which is the whole point of putting him there.
 */
function visibleRoof() {
  const seen = state.skyline.filter(sp => {
    const x = sp.x + 17;

    // On screen at all. The skyline is 1444 wide on a 1280 canvas, so the last
    // spires are roofs nobody can see and nobody can stand on — and a signal lit
    // off the right edge is a whole scene that never happens.
    if (x < 50 || x > W - 50) return false;

    const top = H - SKYLINE_HEIGHT - sp.h;

    // Every tower over the figure's column, not just the first one found: a spire
    // is 35 wide and the towers are 55 to 105, so one spire can cross two of them,
    // and taking the first lets it slip through the gap beside a short one while a
    // tall one stands in front. The highest roof is the one that decides.
    const column = [x - 4, x + 4];
    const tops = state.buildings
      .filter(b => b.x + b.w > column[0] && b.x < column[1])
      .map(b => b.y);
    const covering = tops.length ? Math.min(...tops) : H;

    // Clearance for the whole figure, not just the roofline.
    return covering - top > 20;
  });

  if (!seen.length) return null;

  const pick = seen[rand(0, seen.length - 1)];
  return { x: pick.x + 17, top: H - SKYLINE_HEIGHT - pick.h };
}

function newBatsignal() {
  const roof = visibleRoof();

  return {
    // Anywhere along the far skyline will do for the light. It is the figure that
    // needs a roof with nothing tall in front of it.
    x: roof ? roof.x : rand(150, W - 150),
    roofY: roof ? roof.top : H - SKYLINE_HEIGHT,
    roof,
    born: state.clock,
  };
}

function advanceBatsignal(now) {
  const b = state.batsignal;

  if (b) {
    if (now - b.born >= BATSIGNAL_LIFE_MS) {
      state.batsignal = null;
      state.nextBatsignal = now + skyGap(BATSIGNAL_GAP_MIN_MS, BATSIGNAL_GAP_MAX_MS);
    }
    return;
  }

  if (now >= state.nextBatsignal) {
    state.batsignal = newBatsignal();
    record('sky', { id: 'batsignal' });
  }
}

/**
 * A bat, centred on (x, y) and `size` px across.
 *
 * A path rather than pixels, unlike almost everything else here: at this size a
 * blocky bat is a smudge, and the shape is all it has to say for itself.
 */
/**
 * The bat, taken from a file when there is one.
 *
 * Drop any black-on-white image at `public/bat.svg` or `public/bat.png` and it
 * becomes the shape in the light: the dark ink turns into the silhouette and the
 * white turns into nothing, which is the one conversion that makes a logo drawing
 * usable as a hole in a beam. Until somebody puts one there, the drawn version
 * below stands in.
 *
 * The files are named in plain strings on purpose rather than imported, so that a
 * missing one is a missing picture and not a failed build.
 */
const batMask = document.createElement('canvas');
let batReady = false;

/**
 * Turns a picture of a mark into a mask: dark ink opaque, everything lighter
 * see-through. Multiplied by what was already there, so a transparent background
 * stays transparent rather than turning into ink — a pixel with no colour has no
 * luminance to read, and reading it as black would invert the whole picture.
 */
function buildBatMask(img) {
  batMask.width = img.naturalWidth;
  batMask.height = img.naturalHeight;

  const mctx = batMask.getContext('2d');
  mctx.drawImage(img, 0, 0);

  const px = mctx.getImageData(0, 0, batMask.width, batMask.height);
  for (let i = 0; i < px.data.length; i += 4) {
    const luma = (px.data[i] * 0.299 + px.data[i + 1] * 0.587 + px.data[i + 2] * 0.114) / 255;
    px.data[i + 3] = Math.round(px.data[i + 3] * (1 - luma));
  }
  mctx.putImageData(px, 0, 0);
}

// The vector first, the bitmap second, and the drawing below last. An SVG is the
// same shape at any size and this one is drawn small, where a bitmap of the emblem
// goes soft at the edges.
const batPictures = ['./bat.svg', './bat.png'];
let batTrying = 0;

function nextBatPicture() {
  if (batTrying >= batPictures.length) return;

  const img = new Image();
  batTrying += 1;

  img.addEventListener('load', () => {
    // A picture with no size of its own — an SVG without width and height — draws
    // nothing and converts to nothing, so it counts as no picture at all.
    if (!img.naturalWidth || !img.naturalHeight) {
      nextBatPicture();
      return;
    }

    try {
      buildBatMask(img);
      batReady = true;
    } catch {
      // A canvas that refuses to be read has no mask to give.
      nextBatPicture();
    }
  });

  img.addEventListener('error', nextBatPicture);
  img.src = batPictures[batTrying - 1];
}

nextBatPicture();

function drawBat(x, y, size, colour, alpha = 1) {
  if (batReady) {
    const w = size;
    const h = size * (batMask.height / batMask.width);

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.drawImage(batMask, x - w / 2, y - h / 2, w, h);
    ctx.restore();
    return;
  }

  const s = size / 2;

  // Built from pieces rather than from one outline, because every attempt at the
  // outline produced a fir tree: curves set point to point kept meeting in spikes.
  // Same colour, so everything unions into one silhouette.
  ctx.fillStyle = colour;
  ctx.strokeStyle = colour;

  // The oval frame, which is what the emblem is: the bat sits inside a ring and the
  // ring is part of the mark. Stroked and left hollow, so the light shows between.
  ctx.lineWidth = Math.max(1.5, s * 0.15);
  ctx.beginPath();
  ctx.ellipse(x, y, s, s * 0.64, 0, 0, Math.PI * 2);
  ctx.stroke();

  // The body: wide and shallow, its ends reaching out towards the ring.
  ctx.beginPath();
  ctx.ellipse(x, y + s * 0.06, s * 0.86, s * 0.44, 0, 0, Math.PI * 2);
  ctx.fill();

  // The ears, as two rounded petals rather than spikes: in this emblem they are
  // soft, and sharp triangles are what made the last version look like a tree.
  [-1, 1].forEach(dir => {
    ctx.beginPath();
    ctx.ellipse(x + dir * s * 0.30, y - s * 0.34, s * 0.15, s * 0.30, dir * 0.22, 0, Math.PI * 2);
    ctx.fill();
  });

  // And four round lobes along the bottom, the middle pair the larger — the
  // underside of this emblem is four bumps, not three points.
  [[-0.21, 0.24, 0.28], [0.21, 0.24, 0.28], [-0.56, 0.20, 0.23], [0.56, 0.20, 0.23]]
    .forEach(([dx, dy, radius]) => {
      ctx.beginPath();
      ctx.arc(x + s * dx, y + s * dy, s * radius, 0, Math.PI * 2);
      ctx.fill();
    });
}

/**
 * Somebody small, standing on a roof. Sixteen pixels tall, so the shape has to do
 * all the work: the ears, the cape, and a belt that catches the light.
 */
function drawTinyHero(x, footY, alpha) {
  // Backlit by the lamp. Without this he is a dark shape on a dark sky, sixteen
  // pixels tall and the same value as the night behind him, which is no figure at
  // all. The halo costs one gradient and is what makes him a silhouette.
  const back = ctx.createRadialGradient(x, footY - 8, 2, x, footY - 8, 17);
  back.addColorStop(0, `rgba(255,226,150,${0.34 * alpha})`);
  back.addColorStop(1, 'rgba(255,226,150,0)');
  ctx.fillStyle = back;
  ctx.beginPath();
  ctx.arc(x, footY - 8, 17, 0, Math.PI * 2);
  ctx.fill();

  const body = `rgba(12,17,30,${alpha})`;
  const edge = `rgba(70,88,126,${alpha})`;

  pixelRect(x - 3, footY - 11, 6, 10, body);
  pixelRect(x - 4, footY - 9, 2, 5, edge);
  pixelRect(x + 3, footY - 9, 2, 5, edge);
  pixelRect(x - 2, footY - 14, 4, 3, body);
  pixelRect(x - 2, footY - 16, fino(1), 2, body);
  pixelRect(x + 1, footY - 16, fino(1), 2, body);
  pixelRect(x - 3, footY - 6, 6, fino(1), `rgba(201,162,60,${alpha})`);
}

/**
 * The bat-signal.
 *
 * Painted before the skyline on purpose: the far roofs then cut across the beam,
 * which is what makes it read as distance. That is also what lets him leave, since
 * he only has to sink past the roofline he is standing on to be swallowed by it.
 */
function drawBatsignal() {
  const b = state.batsignal;
  if (!b) return;

  const progress = clamp((state.clock - b.born) / BATSIGNAL_LIFE_MS, 0, 1);

  // Up quickly, hold, then stutter out. A switch being thrown is not a fade.
  let fade = progress < 0.1 ? progress / 0.1 : progress > 0.85 ? (1 - progress) / 0.15 : 1;
  if (progress > 0.85 && Math.sin(state.clock / 55) > 0.1) fade *= 0.35;

  const beam = ctx.createLinearGradient(0, b.roofY, 0, BATSIGNAL_Y);
  beam.addColorStop(0, 'rgba(255,226,150,0)');
  beam.addColorStop(1, `rgba(255,232,168,${0.34 * fade})`);

  ctx.beginPath();
  ctx.moveTo(b.x - 5, b.roofY);
  ctx.lineTo(b.x + 5, b.roofY);
  ctx.lineTo(b.x + BATSIGNAL_SPREAD, BATSIGNAL_Y + 12);
  ctx.lineTo(b.x - BATSIGNAL_SPREAD, BATSIGNAL_Y + 12);
  ctx.closePath();
  ctx.fillStyle = beam;
  ctx.fill();

  const glow = ctx.createRadialGradient(b.x, BATSIGNAL_Y, 2, b.x, BATSIGNAL_Y, BATSIGNAL_GLOW);
  glow.addColorStop(0, `rgba(255,238,186,${0.82 * fade})`);
  glow.addColorStop(0.55, `rgba(255,226,150,${0.3 * fade})`);
  glow.addColorStop(1, 'rgba(255,226,150,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(b.x, BATSIGNAL_Y, BATSIGNAL_GLOW, 0, Math.PI * 2);
  ctx.fill();

  // The bat is the hole in the light rather than a thing painted on top of it.
  if (fade > 0.01) {
    drawBat(b.x, BATSIGNAL_Y, BATSIGNAL_GLOW * 1.05, `rgba(9,13,34,${0.92 * fade})`, 0.92 * fade);
  }

  if (!b.roof) return;

  const since = state.clock - b.born - BATSIGNAL_HERO_DELAY_MS;
  if (since < 0) return;

  const leaving = clamp((since - (BATSIGNAL_LIFE_MS - BATSIGNAL_HERO_DELAY_MS - BATSIGNAL_HERO_EXIT_MS)) / BATSIGNAL_HERO_EXIT_MS, 0, 1);
  if (leaving >= 1) return;

  drawTinyHero(b.roof.x, b.roof.top + leaving * BATSIGNAL_HERO_DROP, fade);
}

/**
 * Cuántas veces más a menudo pasa el cielo por delante en el escaparate.
 *
 * Las esperas entre una cosa y la siguiente son de casi un minuto para las grandes (la ISS, el
 * avión, el cohete), que es lo que una partida pide: un cielo que se cruza constantemente tapa el
 * tiro que estás mirando. En la portada, en cambio, el cielo ES lo que está enseñando el juego, y
 * el doble de a menudo es lo que hace que se vea sin quedarse a mirar.
 *
 * Factor y no valor: el escaparate multiplica la espera que pide el juego.
 */
const ATTRACT_SKY = 2;

/** La espera hasta la próxima cosa del cielo, en el escaparate la mitad de larga. */
function skyGap(min, max) {
  const k = isAttract() ? ATTRACT_SKY : 1;
  return rand(min / k, max / k);
}

/**
 * Moves the transient sky. Called once per frame by the frame composer, next to
 * updateFx().
 */
export function updateSky() {
  // CLASSIC keeps the sky the way the original had it: stars, and nothing else
  // crossing them.
  //
  // Y el escaparate tampoco: la portada no puede quedarse con el cielo que pide un modo guardado
  // hace tres días. Ahí el cielo se ve siempre, y pasa al doble (ver skyGap).
  if (!mods().sky && !isAttract()) return;

  const now = state.clock;

  // A zero schedule means "not armed yet", so the first pass sets every clock.
  if (!state.nextMeteor) state.nextMeteor = now + skyGap(METEOR_GAP_MIN_MS, METEOR_GAP_MAX_MS);
  if (!state.nextIss) state.nextIss = now + skyGap(ISS_GAP_MIN_MS, ISS_GAP_MAX_MS);
  if (!state.nextRocket) state.nextRocket = now + skyGap(ROCKET_GAP_MIN_MS, ROCKET_GAP_MAX_MS);
  if (!state.nextBanner) state.nextBanner = now + skyGap(BANNER_GAP_MIN_MS, BANNER_GAP_MAX_MS);
  if (!state.nextAirforce) state.nextAirforce = now + skyGap(AIRFORCE_GAP_MIN_MS, AIRFORCE_GAP_MAX_MS);
  if (!state.nextBatsignal) state.nextBatsignal = now + skyGap(BATSIGNAL_GAP_MIN_MS, BATSIGNAL_GAP_MAX_MS);

  advanceMeteors(now);
  advanceIss(now);
  advanceRocket(now);
  advanceBanner(now);
  advanceAirforce(now);
  advanceBatsignal(now);
}

/** Shooting stars: a bright head dragging a tail that fades out behind it. */
function drawMeteors() {
  state.meteors.forEach(m => {
    const progress = clamp((state.clock - m.born) / METEOR_LIFE_MS, 0, 1);
    // Fade in and straight back out, so it never pops on or off.
    const alpha = Math.sin(progress * Math.PI) * 0.85;

    const tx = m.x - m.vx * METEOR_TAIL;
    const ty = m.y - m.vy * METEOR_TAIL;

    ctx.save();
    const tail = ctx.createLinearGradient(tx, ty, m.x, m.y);
    tail.addColorStop(0, 'rgba(180, 215, 255, 0)');
    tail.addColorStop(1, `rgba(235, 246, 255, ${alpha})`);

    ctx.strokeStyle = tail;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(m.x, m.y);
    ctx.stroke();
    ctx.restore();

    pixelRect(m.x - 1, m.y - 1, 3, 3, `rgba(255, 255, 255, ${alpha})`);
  });
}

/**
 * The station crossing the sky: a few pixels at a quarter opacity, with a slow
 * flare as it tumbles. Most passes you will not even notice.
 */
function drawIss() {
  const s = state.iss;
  if (!s) return;

  const flare = 0.85 + 0.15 * Math.sin(state.clock / 850);

  ctx.save();
  ctx.globalAlpha = clamp(ISS_ALPHA * flare, 0, 1);
  pixelRect(s.x - 4, s.y, 9, fino(1), '#cfe0ff'); // solar arrays
  pixelRect(s.x - 1, s.y - 1, 3, 3, '#ffffff'); // body
  ctx.restore();
}

// --- the rocket ------------------------------------------------------------

/** Half the time a launch, half the time a booster coming home. */
function newRocket() {
  const launch = Math.random() < 0.5;

  return {
    // Kept toward the middle, so it never crosses the score panels.
    x: rand(280, W - 280),
    y: launch ? rand(280, 360) : -50,
    vy: launch ? 0.8 : 3.6,
    launch,
    puffs: [],
    puffedAt: 0,
  };
}

function advanceRocket(now) {
  const r = state.rocket;

  if (!r) {
    if (now >= state.nextRocket) {
      state.rocket = newRocket();
      record('sky', { id: 'rocket' });
    }
    return;
  }

  if (r.launch) {
    // Climbs, and keeps accelerating on its way out of frame.
    r.vy = Math.min(2, r.vy + 0.012);
    r.y -= r.vy;
  } else {
    // The booster comes in fast and throttles down to a soft touchdown. The
    // speed depends on how far down it already is, so it eases off by itself.
    const progress = clamp((r.y + 50) / 380, 0, 1);
    r.vy = 0.6 + 3 * (1 - progress);
    r.y += r.vy;
  }

  // Exhaust left behind it, at a fixed cadence so the trail is even.
  if (now - r.puffedAt > ROCKET_PUFF_EVERY_MS) {
    r.puffedAt = now;
    r.puffs.push({ x: r.x + rand(-2, 2), y: r.y + 10, born: now, r: rand(2, 4) });
  }
  r.puffs = r.puffs.filter(p => now - p.born < ROCKET_PUFF_LIFE_MS);

  if (r.launch ? r.y < -50 : r.y > 380) {
    state.rocket = null;
    state.nextRocket = now + skyGap(ROCKET_GAP_MIN_MS, ROCKET_GAP_MAX_MS);
  }
}

/**
 * A small rocket: nose cone, fins and all. It is drawn here, with the rest of
 * the sky, so the city passes in front of it — which is what makes a launch
 * look like it is climbing away from behind the skyline.
 */
function drawRocket() {
  const r = state.rocket;
  if (!r) return;

  r.puffs.forEach(p => {
    const a = clamp(1 - (state.clock - p.born) / ROCKET_PUFF_LIFE_MS, 0, 1) * 0.34;
    ctx.globalAlpha = a;
    ctx.fillStyle = '#cfd8e8';
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r + (1 - a) * 5, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.globalAlpha = 1;

  const x = Math.round(r.x);
  const y = Math.round(r.y);

  // Engine flame, flickering like something is actually burning.
  const flame = 6 + Math.abs(Math.sin(state.clock / 55)) * 7;
  ctx.fillStyle = '#ffb057';
  ctx.beginPath();
  ctx.moveTo(x - 3, y + 8);
  ctx.lineTo(x + 3, y + 8);
  ctx.lineTo(x, y + 8 + flame);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#fff0c2';
  ctx.beginPath();
  ctx.moveTo(x - 1.5, y + 8);
  ctx.lineTo(x + 1.5, y + 8);
  ctx.lineTo(x, y + 8 + flame * 0.55);
  ctx.closePath();
  ctx.fill();

  // Landing legs swing out on the way down. Two steps each so they splay
  // outward, and a darker grey than the fins so they actually read.
  if (!r.launch && r.y > 120) {
    pixelRect(x - 6, y + 5, 2, 2, '#9aa6b8');
    pixelRect(x - 8, y + 7, 2, 3, '#9aa6b8');
    pixelRect(x + 5, y + 5, 2, 2, '#9aa6b8');
    pixelRect(x + 7, y + 7, 2, 3, '#9aa6b8');
  }

  pixelRect(x - 2, y - 8, 5, 16, '#e9eef6'); // body
  pixelRect(x - 1, y - 11, 3, 3, '#f2f6fb'); // shoulder of the nose cone
  pixelRect(x, y - 13, 1, 2, '#f8fbff'); // tip
  pixelRect(x - 4, y + 3, 2, 5, '#c2cbd9'); // fins
  pixelRect(x + 3, y + 3, 2, 5, '#c2cbd9');
  pixelRect(x - 1, y - 3, 2, 2, '#2b3a55'); // window
}

// --- the banner plane ------------------------------------------------------

/** A plane towing one of the banner messages, entering from whichever side it likes. */
function newBanner() {
  const dir = Math.random() < 0.5 ? -1 : 1;

  return {
    // Starts well off-screen, so the banner is not already across the middle.
    x: dir > 0 ? -BANNER_TOW - BANNER_WIDTH : W + BANNER_TOW + BANNER_WIDTH,
    y: rand(80, 118),
    vx: BANNER_SPEED * dir,
    dir,
    // The index, not the words. The banner is resolved as it is painted, so a change
    // of language catches a plane that is already in the air — and that is what the
    // drawing has always done. The trouble was that this stored the finished text in
    // a field called `text` while the painter read one called `advert`, so the key
    // came out as banner.undefined and stayed that way.
    advert: rand(0, BANNER_COUNT - 1),
  };
}

function advanceBanner(now) {
  const b = state.banner;

  if (!b) {
    if (now >= state.nextBanner) {
      state.banner = newBanner();
      record('sky', { id: 'banner' });
    }
    return;
  }

  b.x += b.vx;

  if (b.x < -BANNER_TOW - BANNER_WIDTH || b.x > W + BANNER_TOW + BANNER_WIDTH) {
    state.banner = null;
    state.nextBanner = now + skyGap(BANNER_GAP_MIN_MS, BANNER_GAP_MAX_MS);
  }
}

/**
 * The plane and the banner it drags. The banner hangs off the tail on a short
 * line, and its text is never mirrored — a banner reads the same from either
 * side, which is the whole point of putting one up there.
 */
function drawBanner() {
  const b = state.banner;
  if (!b) return;

  const x = Math.round(b.x);
  const y = Math.round(b.y);
  const d = b.dir;
  const bx = x - d * BANNER_TOW;
  const by = y + 4;

  // A part's near edge, measured out from the centre and mirrored by heading.
  const box = (off, w, oy, h, colour) => {
    pixelRect(d > 0 ? x + off : x - off - w, y + oy, w, h, colour);
  };

  ctx.save();

  // tow line
  ctx.strokeStyle = 'rgba(196, 212, 236, 0.5)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x - d * 10, by - 3);
  ctx.lineTo(bx + (d * BANNER_WIDTH) / 2, by - 3);
  ctx.stroke();

  // the banner itself
  ctx.fillStyle = 'rgba(236, 242, 250, 0.94)';
  ctx.fillRect(bx - BANNER_WIDTH / 2, by - BANNER_HEIGHT / 2, BANNER_WIDTH, BANNER_HEIGHT);
  ctx.fillStyle = '#1d2536';
  // El cuerpo de la letra también, aunque no sea píxel: es un rótulo para leerse, y a 10px de
  // mundo se quedaba en 8 de pantalla. Ver `fino`.
  ctx.font = 'bold 13px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(t('banner.' + b.advert), bx, by + 1);

  // the aircraft
  box(-9, 18, -1, 3, '#dfe6f2'); // fuselage
  box(9, 3, -2, 3, '#eef3fa'); // nose
  box(-12, 3, -5, 4, '#c3ccdb'); // tail fin
  pixelRect(x - 2, y - 5, 5, 10, '#c3ccdb'); // wing
  box(4, 2, -1, fino(1), '#2b3a55'); // window
  box(9, fino(1), -4, 8, 'rgba(206, 220, 240, 0.55)'); // propeller blur

  ctx.restore();
}

// --- air force one ---------------------------------------------------------

/** The presidential aircraft, crossing high up from whichever side it likes. */
function newAirforce() {
  const dir = Math.random() < 0.5 ? -1 : 1;

  return {
    x: dir > 0 ? -70 : W + 70,
    y: rand(72, 100),
    vx: AIRFORCE_SPEED * dir,
    dir,
  };
}

function advanceAirforce(now) {
  const a = state.airforce;

  if (!a) {
    if (now >= state.nextAirforce) {
      state.airforce = newAirforce();
      record('sky', { id: 'airforce' });
    }
    return;
  }

  a.x += a.vx;

  if (a.x < -90 || a.x > W + 90) {
    state.airforce = null;
    state.nextAirforce = now + skyGap(AIRFORCE_GAP_MIN_MS, AIRFORCE_GAP_MAX_MS);
  }
}

/** One of the escorts: barely a dozen pixels, but the shape still reads as a jet. */
function drawFighter(fx, fy, d) {
  const f = (off, w, oy, h, colour) =>
    pixelRect(d > 0 ? fx + off : fx - off - w, fy + oy, w, h, colour);

  // Todo el caza es de una raya de alto, y a 0,8 se perdía entero. Ver `fino`.
  f(-6, 12, 0, fino(1), '#b8c4d4'); // fuselage
  f(6, 2, 0, fino(1), '#d2dbe7'); // nose
  f(-1, 5, 1, fino(1), '#9aa8ba'); // wing
  f(-7, 2, -3, 3, '#93a1b3'); // fin
}

/**
 * Air Force One. What makes it that, rather than "a blue plane", is the livery,
 * the upper-deck hump and the four engines. The two fighters are not decoration:
 * the escort is the thing people actually recognise.
 *
 * Every colour here is picked against a night sky. The real tail fin is a deep
 * navy, which on this background is invisible — so it is a good deal lighter
 * than life, because an unreadable fin is an unreadable aircraft.
 */
function drawAirforce() {
  const a = state.airforce;
  if (!a) return;

  const x = Math.round(a.x);
  const y = Math.round(a.y);
  const d = a.dir;

  // A part's near edge, measured out from the centre and mirrored by heading.
  const box = (off, w, oy, h, colour) =>
    pixelRect(d > 0 ? x + off : x - off - w, y + oy, w, h, colour);

  // Escort, trailing and offset either side, one higher than the other.
  drawFighter(x - d * AIRFORCE_ESCORT_SIDE, y - AIRFORCE_ESCORT_DROP, d);
  drawFighter(x - d * (AIRFORCE_ESCORT_SIDE + 7), y + AIRFORCE_ESCORT_DROP + 4, d);

  // Swept wing, stepped in two so the sweep reads even this small. Kept a step
  // darker than the fuselage, or the two merge into one white smear.
  box(-2, 14, 3, 2, '#c3d1e3');
  box(-9, 8, 5, 2, '#aebfd4');

  // Four engines, hanging under the wing.
  box(1, 4, 5, 2, '#9dabbe');
  box(-6, 4, 6, 2, '#9dabbe');

  // Fuselage: white over a pale blue belly, with the stripe between the two.
  box(-24, 41, 0, 2, '#eef3fa');
  box(-24, 41, 2, fino(1), '#7ba7d4');
  box(-24, 41, 3, 2, '#c2d6ee');

  box(9, 9, -2, 3, '#eef3fa'); // the 747's upper-deck hump
  box(17, 4, 0, 4, '#e6edf7'); // nose, rounded off in two steps
  box(21, 2, 1, 2, '#dbe5f2');
  box(15, 3, 1, 1, '#26344f'); // cockpit glass

  // Tail fin. The give-away, and the one part that has to survive the dark.
  box(-26, 5, -10, 11, '#40689f');
  box(-26, 2, -10, 11, '#5f8cc4'); // lit leading edge
  box(-23, 8, 3, 2, '#d6e1ee'); // horizontal stabiliser
}
